import React, { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Hammer,
  MapPinned,
  Mail,
  Settings,
  LogOut,
  Search,
  RefreshCw,
  ArrowUpRight,
  PackageCheck,
  Gift,
  CheckCircle2,
  Wallet,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  Brand,
  Button,
  ErrorMessage,
  Field,
  TreePine,
  ArrowRight,
} from "./components.jsx";
import { money, PACKAGES } from "../shared/catalog.js";
import { api } from "./api.js";
import { OrderDetails } from "./workshop-order.jsx";
import { BuildWorkshop } from "./workshop-build.jsx";
import { DeliveryMap } from "./workshop-map.jsx";
import { WorkshopSettings, WorkshopMail } from "./workshop-settings.jsx";

export function Admin({ navigate }) {
  const [admin, setAdmin] = useState(undefined),
    [profiles, setProfiles] = useState(null),
    [tab, setTab] = useState("orders");
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(false),
    [selected, setSelected] = useState(null);
  async function refresh() {
    setLoading(true);
    setError("");
    try {
      setData(await api("/api/admin/orders"));
    } catch (e) {
      setError(e.message);
      if (e.status === 401) setAdmin(null);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    api("/api/session")
      .then((v) => {
        setProfiles(v.profileLogin ? v.profiles : null);
        setAdmin(v.admin);
      })
      .catch(() => {
        setAdmin(null);
        setError("The workshop couldn’t connect. Please try signing in.");
      });
  }, []);
  useEffect(() => {
    if (admin) refresh();
  }, [admin?.id]);
  async function update(order, patch) {
    try {
      const result = await api(`/api/admin/orders/${order.id}`, {
        method: "PATCH",
        body: { version: order.version, ...patch },
      });
      setData((d) => ({
        ...d,
        orders: d.orders.map((o) => (o.id === order.id ? result.order : o)),
      }));
      return result.order;
    } catch (e) {
      if (e.status === 401) setAdmin(null);
      throw e;
    }
  }
  async function locate(order) {
    const result = await api(`/api/admin/orders/${order.id}/locate`, {
      method: "POST",
      body: {},
    });
    setData((d) => ({
      ...d,
      orders: d.orders.map((o) => (o.id === order.id ? result.order : o)),
    }));
    return result.order;
  }
  if (admin === undefined)
    return (
      <div className="workshop-loading">
        <TreePine />
        <p>Opening the workshop…</p>
      </div>
    );
  if (!admin)
    return (
      <WorkshopLogin
        profiles={profiles}
        onLogin={setAdmin}
        navigate={navigate}
        initialError={error}
      />
    );
  const ready = data?.orders.filter((o) => o.status === "ready") || [];
  const names = {
    orders: "The Christmas workshop",
    build: "Build a little Christmas magic",
    deliveries: "Plan your doorstep deliveries",
    mail: "The Christmas post",
    settings: "Make room for magic",
  };
  return (
    <div className="workshop">
      <aside className="workshop-sidebar">
        <Brand />
        <span className="workshop-label">THE WORKSHOP · 2026</span>
        <nav aria-label="Workshop navigation">
          {[
            ["orders", LayoutDashboard, "Orders"],
            ["build", Hammer, "Build"],
            ["deliveries", MapPinned, "Delivery map"],
            ["mail", Mail, "Email outbox"],
            ["settings", Settings, "Season settings"],
          ].map(([id, Icon, label]) => (
            <button
              key={id}
              className={tab === id ? "active" : ""}
              onClick={() => {
                setTab(id);
                setError("");
              }}
            >
              <Icon size={19} />
              {label}
              {id === "deliveries" && ready.length > 0 && (
                <span className="sidebar-count">{ready.length}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="sidebar-season">
          <TreePine size={24} strokeWidth={1} />
          <strong>
            A little magic,
            <br />
            one family at a time.
          </strong>
          <span>DELIVERY DECEMBER 6–12</span>
        </div>
        <div className="sidebar-account">
          <span className="avatar">{admin.name[0]}</span>
          <div>
            <strong>{admin.name.split(" ")[0]}</strong>
            <small>Founder & chief magic-maker</small>
          </div>
          <button
            aria-label="Sign out"
            className="icon-button"
            onClick={async () => {
              await api("/api/logout", { method: "POST", body: {} });
              setAdmin(null);
            }}
          >
            <LogOut size={17} />
          </button>
        </div>
      </aside>
      <div className="workshop-main">
        <header className="workshop-header">
          <span>
            WHIMSY DELIVERED <i>/</i>{" "}
            {
              {
                orders: "ORDERS",
                build: "BUILD",
                deliveries: "DELIVERIES",
                mail: "OUTBOX",
                settings: "SETTINGS",
              }[tab]
            }
          </span>
          <button className="text-link" onClick={() => navigate("/")}>
            View the website <ArrowUpRight size={15} />
          </button>
        </header>
        <div className="workshop-content">
          <div className="workshop-title">
            <div>
              <span className="eyebrow">
                HELLO, {admin.name.split(" ")[0].toUpperCase()} ✧
              </span>
              <h1>{names[tab]}</h1>
              <p>
                {tab === "orders"
                  ? "All the little details behind the big Christmas smiles."
                  : tab === "build"
                    ? "One order, one clear step at a time."
                    : tab === "deliveries"
                      ? "See ready orders, choose your stops, and plan your own route."
                      : tab === "mail"
                        ? "Order confirmations, payment receipts, and delivered messages."
                        : "Open or close the season and keep collection points up to date."}
              </p>
            </div>
            <button
              className="button button-outline button-small"
              disabled={loading}
              onClick={refresh}
            >
              <RefreshCw size={15} className={loading ? "spin" : ""} />
              {loading ? "Refreshing…" : "Refresh orders"}
            </button>
          </div>
          <ErrorMessage>{error}</ErrorMessage>
          {data ? (
            <>
              {tab === "orders" && (
                <Orders
                  orders={data.orders}
                  admins={data.admins}
                  admin={admin}
                  settings={data.settings}
                  onSelect={setSelected}
                  onMap={() => setTab("deliveries")}
                />
              )}
              {tab === "build" && (
                <BuildWorkshop
                  admin={admin}
                  onUpdate={update}
                  onChanged={refresh}
                />
              )}
              {tab === "deliveries" && (
                <DeliveryMap
                  orders={ready}
                  admins={data.admins}
                  admin={admin}
                  routes={data.routes || []}
                  onChanged={refresh}
                  onSelect={setSelected}
                  onUpdate={update}
                  onLocate={locate}
                />
              )}
              {tab === "mail" && <WorkshopMail />}
              {tab === "settings" && (
                <WorkshopSettings
                  settings={data.settings}
                  emailEnabled={data.emailEnabled}
                  onSave={(settings) => setData((d) => ({ ...d, settings }))}
                />
              )}
            </>
          ) : (
            <div className="empty-state">
              <TreePine />
              <p>
                {loading
                  ? "Gathering your orders…"
                  : "Orders aren’t loaded yet."}
              </p>
              {!loading && <Button onClick={refresh}>Try again</Button>}
            </div>
          )}
        </div>
      </div>
      {selected && data?.orders.find((o) => o.id === selected) && (
        <OrderDetails
          order={data.orders.find((o) => o.id === selected)}
          admin={admin}
          admins={data.admins}
          onUpdate={update}
          onLocate={locate}
          onClose={() => setSelected(null)}
          onBuild={() => {
            setSelected(null);
            setTab("build");
          }}
        />
      )}
    </div>
  );
}

function WorkshopLogin({ profiles, onLogin, navigate, initialError }) {
  const [username, setUsername] = useState("maddie"),
    [password, setPassword] = useState(""),
    [show, setShow] = useState(false),
    [error, setError] = useState(initialError),
    [busy, setBusy] = useState(false);
  async function submit(e, profile) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api("/api/login", {
        method: "POST",
        body: profile ? { username: profile } : { username, password },
      });
      onLogin(r.admin);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="login-page">
      <button className="login-home text-link" onClick={() => navigate("/")}>
        ← Back to the Christmas corner
      </button>
      <div className="login-art">
        <img
          src="/images/christmas-doorstep.png"
          alt="A Christmas surprise waiting on a doorstep"
        />
        <div>
          <span className="script">A little behind-the-scenes</span>
          <h2>
            Where the
            <br />
            magic comes together.
          </h2>
        </div>
      </div>
      <div className="login-form-wrap">
        <Brand />
        <div className="login-intro">
          <span className="eyebrow">FOR OUR TWO CHIEF MAGIC-MAKERS</span>
          <h1>
            Welcome to
            <br />
            <em>the workshop.</em>
          </h1>
          <p>
            {profiles
              ? "Who’s in the workshop today?"
              : "Sign in to get the Christmas surprises ready."}
          </p>
        </div>
        {profiles ? (
          <div className="profile-choices">
            {profiles.map((p) => (
              <button
                key={p.id}
                className="profile-choice"
                aria-label={`Continue as ${p.name.split(" ")[0]}`}
                disabled={busy}
                onClick={(e) => submit(e, p.id)}
              >
                <span className="avatar">{p.name[0]}</span>
                <strong>{p.name.split(" ")[0]}</strong>
                <ArrowRight size={17} />
              </button>
            ))}
            <ErrorMessage>{error}</ErrorMessage>
          </div>
        ) : (
        <form onSubmit={submit}>
          <Field label="Who’s in the workshop?">
            <select
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            >
              <option value="maddie">Maddy</option>
              <option value="mackenzie">Mackenzie</option>
            </select>
          </Field>
          <Field label="Password" controlId="workshop-password">
            <span className="password-field">
              <input
                id="workshop-password"
                name="password"
                type={show ? "text" : "password"}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                aria-label={show ? "Hide password" : "Show password"}
                onClick={() => setShow(!show)}
              >
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
          </Field>
          <ErrorMessage>{error}</ErrorMessage>
          <Button className="full-width" disabled={busy}>
            {busy ? "Opening the workshop…" : "Step into the workshop"}
            <ArrowRight size={17} />
          </Button>
        </form>
        )}
        <p className="fine-print">Private access for Maddy & Mackenzie.</p>
      </div>
    </div>
  );
}

function Orders({ orders, admins, admin, settings, onSelect, onMap }) {
  const [filter, setFilter] = useState("all"),
    [query, setQuery] = useState("");
  const received = orders.reduce((n, o) => n + o.received, 0);
  const unpaid = orders.filter((o) => o.received < o.total);
  const visible = orders.filter(
    (o) =>
      (filter === "all" ||
        (filter === "mine" && o.assignedTo === admin.id) ||
        (filter === "unpaid" && o.received < o.total) ||
        filter === o.status) &&
      (!query ||
        [
          o.number,
          o.customer.name,
          o.customer.address,
          o.customer.city,
          o.customer.email,
          ...o.items.flatMap((i) => i.children.map((c) => c.name)),
        ]
          .join(" ")
          .toLowerCase()
          .includes(query.toLowerCase())),
  );
  return (
    <>
      <div className="stat-grid">
        {[
          [
            Gift,
            "Family orders",
            orders.length,
            "A little Christmas, reserved.",
          ],
          [
            Wallet,
            "Money in",
            money(received),
            `${unpaid.length} awaiting full payment`,
          ],
          [
            PackageCheck,
            "Ready for delivery",
            orders.filter((o) => o.status === "ready").length,
            "Packed up with a little love.",
          ],
          [
            CheckCircle2,
            "Magic delivered",
            orders.filter((o) => o.status === "delivered").length,
            "Doorstep surprises, complete.",
          ],
        ].map(([Icon, title, value, note]) => (
          <div className="stat-card" key={title}>
            <div>
              <span>{title}</span>
              <Icon size={19} />
            </div>
            <strong>{value}</strong>
            <small>{note}</small>
          </div>
        ))}
      </div>
      <div
        className={`season-status ${settings.ordersOpen && new Date() < new Date(settings.deadline) ? "" : "season-closed"}`}
      >
        <span className="status-dot" />
        <span>
          {settings.ordersOpen && new Date() < new Date(settings.deadline)
            ? "The season is open. New orders are welcome."
            : "The season is closed. Checkout is unavailable."}
        </span>
        <span>
          Orders & letters close{" "}
          {new Intl.DateTimeFormat("en-US", {
            month: "long",
            day: "numeric",
            timeZone: "America/Boise",
          }).format(new Date(settings.deadline).getTime() - 1)}
        </span>
      </div>
      <section className="orders-panel">
        <div className="orders-toolbar">
          <div>
            <h2>A little list of Christmas magic</h2>
            <p>
              {orders.length} family {orders.length === 1 ? "order" : "orders"}{" "}
              this season
            </p>
          </div>
          <button
            className="button button-outline button-small"
            onClick={onMap}
          >
            <MapPinned size={16} /> Delivery map
          </button>
        </div>
        <div className="orders-filters">
          <div className="filter-tabs">
            {[
              ["all", "All orders"],
              ["new", "New"],
              ["mine", "Mine"],
              ["preparing", "Preparing"],
              ["ready", "Ready"],
              ["delivered", "Delivered"],
              ["unpaid", "Unpaid"],
            ].map(([id, label]) => (
              <button
                className={filter === id ? "active" : ""}
                key={id}
                onClick={() => setFilter(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="search-box">
            <Search size={16} />
            <input
              aria-label="Search orders"
              placeholder="Name, address or order…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
        </div>
        <div className="orders-table-wrap">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order & family</th>
                <th>The little surprise</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Magic-maker</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => (
                <tr key={o.id}>
                  <td>
                    <button
                      className="order-family"
                      onClick={() => onSelect(o.id)}
                    >
                      <strong>{o.customer.name}</strong>
                      <span>
                        {o.number} <i>·</i> {o.customer.city}
                      </span>
                    </button>
                  </td>
                  <td>
                    <strong>
                      {PACKAGES.find((p) => p.id === o.items[0].packageId).name}
                      {o.items.length > 1 ? ` +${o.items.length - 1}` : ""}
                    </strong>
                    <span>
                      {o.items.reduce((n, i) => n + i.children.length, 0)}{" "}
                      children · {money(o.total)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${o.received >= o.total ? "badge-paid" : "badge-cash"}`}
                    >
                      {o.received >= o.total
                        ? "Paid"
                        : o.received > 0
                          ? "Part paid"
                          : "Awaiting payment"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge-${o.status}`}>
                      {o.status === "new"
                        ? "New order"
                        : o.status === "ready"
                          ? "Ready to deliver"
                          : o.status[0].toUpperCase() + o.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    {o.assignedTo ? (
                      <span className="assigned-name">
                        <span className="avatar avatar-small">
                          {o.assignedTo[0].toUpperCase()}
                        </span>
                        {
                          admins
                            .find((a) => a.id === o.assignedTo)
                            ?.name.split(" ")[0]
                        }
                      </span>
                    ) : (
                      <span className="unclaimed">Unclaimed</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="icon-button"
                      aria-label={`View ${o.number}`}
                      onClick={() => onSelect(o.id)}
                    >
                      <ArrowUpRight size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!visible.length && (
          <div className="empty-state">
            <Gift size={37} strokeWidth={1} />
            <h3>
              {orders.length
                ? "No orders match this view."
                : "The first little surprise starts here."}
            </h3>
            <p>
              {orders.length
                ? "Try another filter or search."
                : "Cash orders will appear here as families place them."}
            </p>
          </div>
        )}
      </section>
      <p className="workshop-footnote">
        ✧ Made with love. Managed one little detail at a time.
      </p>
    </>
  );
}
